import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { TixAnalyticsService } from '@tix/shared/analytics';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  Maybe,
  TixCompany,
  TixGetCompaniesByCompanyIdsQuery
} from '@tix/data-access';
import { Roles } from '@tix/auth/state';
import { map } from 'rxjs/operators';
import { TixStaffService } from '@tix/staff/state';
import { Observable } from 'rxjs';

function firstOrNull<T>(array: Array<T> = []): T | null {
  return array[0] ?? null;
}

type CompanyCardData = TixGetCompaniesByCompanyIdsQuery['Company'][number];
type CompanyCardPhoneNumber =
  CompanyCardData['phoneNumbers'][number]['phoneNumber'];
type CompanyCardAddress = CompanyCardData['addresses'][number]['address'];
type CompanyCardMediaFile = CompanyCardData['mediaFiles'][number];
@Component({
  selector: 'tix-company-card',
  templateUrl: './company-card.component.html',
  styleUrls: ['./company-card.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'tix-company-card'
  }
})
export class TixCompanyCardComponent implements OnInit {
  @Input()
  company: CompanyCardData;

  link$: Observable<string[]>;

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private staffService: TixStaffService,
    private analytics: TixAnalyticsService
  ) {}

  get phoneNumber(): Maybe<CompanyCardPhoneNumber> {
    return firstOrNull(this.company.phoneNumbers)?.phoneNumber;
  }

  get address(): Maybe<CompanyCardAddress> {
    return firstOrNull(this.company.addresses)?.address;
  }

  get companyLogo(): Maybe<string> {
    return (
      getCompanyLogoFromMediaFiles(this.company.mediaFiles)?.mediaFile
        ?.location ??
      'https://asylumtixmedia.sfo3.digitaloceanspaces.com/asylumTix/default_images/ticket_logo.jpg'
    );
  }

  ngOnInit(): void {
    this.link$ = this.staffService
      .getCurrentUserRolesInCompany(this.company.companyId)
      .pipe(
        map(roles => {
          if (roles.includes(Roles.CO_ADMIN)) {
            return [this.company.companyId, 'dashboard'];
          } else if (roles.includes(Roles.BO_USER)) {
            return [this.company.companyId, 'dashboard'];
          } else if (roles.includes(Roles.EVENT_ADMINISTRATOR)) {
            return [this.company.companyId, 'events'];
          } else {
            return [this.company.companyId, 'reports'];
          }
        })
      );
  }

  openCompany(): void {
    this.analytics.captureEvent('company_card_clicked', {
      company_id: this.company?.companyId,
      company_name: this.company?.name
    });
  }

  formattedAddress(): string {
    return [
      this.address?.streetAddress,
      this.address?.city,
      `${this.address?.stateProvince ?? ''} ${
        this.address?.postalCode ?? ''
      }`.trim()
    ]
      .filter(v => !!v)
      .join(', ');
  }
}

function getCompanyLogoFromMediaFiles(mediafiles: CompanyCardMediaFile[]) {
  return mediafiles?.find(e => e.mediaFile.fileType === 'Company Logo') ?? null;
}
